import { ComponentType, lazy, LazyExoticComponent, Suspense } from 'react';

import { useDrawerStore } from './drawer-manager.store';
import {
  DRAWER_REGISTRY,
  DrawerId,
  DrawerInjectedProps,
} from './drawer-registry';
import { DrawerStackProvider } from './drawer-stack.context';

/**
 * The registry is type-checked per entry at its declaration; the manager
 * renders every drawer through one erased signature.
 */
type AnyDrawerComponent = ComponentType<
  DrawerInjectedProps & Record<string, unknown>
>;

const lazyCache = new Map<DrawerId, LazyExoticComponent<AnyDrawerComponent>>();

function getLazyComponent(
  id: DrawerId,
): LazyExoticComponent<AnyDrawerComponent> | null {
  if (!lazyCache.has(id)) {
    const entry = DRAWER_REGISTRY[id];
    if (!entry) return null;
    // Erasure point: entries are checked against `DrawerPropsMap` where they
    // are declared, so the manager only needs the injected props here.
    lazyCache.set(id, lazy(entry.loader));
  }
  return lazyCache.get(id) ?? null;
}

const DrawerManager = () => {
  const stack = useDrawerStore((s) => s.stack);
  const close = useDrawerStore((s) => s.close);

  return (
    <>
      {stack.map((entry, index) => {
        const LazyDrawer = getLazyComponent(entry.id);
        if (!LazyDrawer) return null;

        return (
          <DrawerStackProvider key={entry.id} depth={index}>
            <Suspense fallback={null}>
              <LazyDrawer
                {...entry.props}
                open={entry.phase === 'open'}
                onClose={() => close(entry.id)}
              />
            </Suspense>
          </DrawerStackProvider>
        );
      })}
    </>
  );
};

export { DrawerManager };
